import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { api, formatCurrency } from '../../utils/api'
import { PRODUCT_TYPES, SHOPIFY_ADMIN_URL } from '../../utils/constants'
import { MetricsRow, MultiSelectDropdown, ProgressBar, SearchLinks } from '../shared'

function InventoryCleanup() {
  const [allItems, setAllItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [error, setError] = useState(null)
  const [collections, setCollections] = useState([])
  const [selectedItems, setSelectedItems] = useState(new Set())
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' })
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const abortControllerRef = useRef(null)

  // Search filters (trigger API call)
  const [searchFilters, setSearchFilters] = useState({
    product_types: [],
    collection_ids: [],
    price_min: '',
    price_max: ''
  })

  // Display options (client-side)
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [limit, setLimit] = useState(100)

  useEffect(() => {
    loadCollections()
    // Don't load items on initial load - wait for user to search
  }, [])

  async function loadCollections() {
    try {
      const response = await api.get('/api/inventory/shopify/collections')
      setCollections(response.collections || [])
    } catch (err) {
      console.error('Failed to load collections:', err)
    }
  }

  const loadItems = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      params.set('stock_status', 'out_of_stock')
      params.set('status', 'active')
      if (searchFilters.product_types.length > 0) params.set('product_types', searchFilters.product_types.join(','))
      if (searchFilters.collection_ids.length > 0) params.set('collection_ids', searchFilters.collection_ids.join(','))
      if (searchFilters.price_min) params.set('price_min', searchFilters.price_min)
      if (searchFilters.price_max) params.set('price_max', searchFilters.price_max)
      params.set('limit', '500')

      const response = await api.get(`/api/inventory/shopify/search?${params}`)
      setAllItems(response.products || [])
      setTotalCount(response.count || 0)
      setSelectedItems(new Set())
    } catch (err) {
      console.error('Failed to load cleanup items:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [searchFilters])

  function handleFilterChange(field, value) {
    setSearchFilters(prev => ({ ...prev, [field]: value }))
  }

  function handleSearch(e) {
    e.preventDefault()
    setHasSearched(true)
    loadItems()
  }

  function clearFilters() {
    setSearchFilters({
      product_types: [],
      collection_ids: [],
      price_min: '',
      price_max: ''
    })
    setSortBy('created_at')
    setSortOrder('desc')
    setLimit(100)
  }

  // Client-side sorting and pagination
  const displayItems = useMemo(() => {
    const sorted = [...allItems].sort((a, b) => {
      let aVal = a[sortBy]
      let bVal = b[sortBy]

      if (['price', 'cost', 'quantity', 'compare_at_price'].includes(sortBy)) {
        aVal = parseFloat(aVal) || 0
        bVal = parseFloat(bVal) || 0
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase()
      if (typeof bVal === 'string') bVal = bVal.toLowerCase()

      if (['created_at', 'updated_at'].includes(sortBy)) {
        aVal = new Date(aVal).getTime()
        bVal = new Date(bVal).getTime()
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return sorted.slice(0, limit)
  }, [allItems, sortBy, sortOrder, limit])

  function handleSort(field) {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  function SortHeader({ field, children, align }) {
    const isActive = sortBy === field
    const arrow = isActive ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''
    return (
      <th
        onClick={() => handleSort(field)}
        style={{ cursor: 'pointer', textAlign: align, userSelect: 'none' }}
        className={isActive ? 'sort-active' : ''}
      >
        {children}{arrow}
      </th>
    )
  }

  function handleSelectAll(e) {
    if (e.target.checked) {
      setSelectedItems(new Set(displayItems.map(item => item.id)))
    } else {
      setSelectedItems(new Set())
    }
  }

  function handleSelectItem(id) {
    setSelectedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) newSet.delete(id)
      else newSet.add(id)
      return newSet
    })
  }

  async function handleUnlistSelected() {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Are you sure you want to unlist ${selectedItems.size} products?\n\nThis will set them to Unlisted status in Shopify. Products will be hidden from collections and search but remain accessible via direct URL.`
    )
    if (!confirmed) return

    setProcessing(true)
    abortControllerRef.current = new AbortController()
    const ids = Array.from(selectedItems)
    let completed = 0
    let errors = []

    for (const id of ids) {
      if (abortControllerRef.current.signal.aborted) break

      setProgress({
        current: completed + 1,
        total: ids.length,
        status: `Unlisting ${completed + 1} of ${ids.length}...`
      })

      try {
        await api.post(`/api/inventory/shopify/unlist/${id}`)
        completed++
      } catch (err) {
        errors.push({ id, error: err.message })
        completed++
      }
    }

    setProcessing(false)
    setProgress({ current: 0, total: 0, status: '' })

    if (errors.length > 0) {
      alert(`Completed with ${errors.length} errors. ${completed - errors.length} products unlisted successfully.`)
    } else {
      alert(`Successfully unlisted ${completed} products.`)
    }

    loadItems()
  }

  function handleCancelOperation() {
    if (abortControllerRef.current) abortControllerRef.current.abort()
  }

  // Check if advanced filters are active
  const hasAdvancedFilters = searchFilters.product_types.length > 0 || searchFilters.collection_ids.length > 0 ||
    searchFilters.price_min || searchFilters.price_max

  // Calculate metrics
  const totalValue = displayItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)
  const totalCost = displayItems.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0)

  const metrics = [
    { label: 'Loaded', value: allItems.length, color: 'blue' },
    { label: 'Total Value', value: totalValue, format: 'currency', color: 'purple' },
    { label: 'Total Cost', value: totalCost, format: 'currency', color: 'amber' },
    { label: 'Selected', value: selectedItems.size, color: 'green' }
  ]

  const collectionOptions = collections.map(c => ({ value: c.id, label: c.title }))
  const productTypeOptions = PRODUCT_TYPES.map(t => ({ value: t, label: t }))

  return (
    <div>
      <MetricsRow metrics={metrics} />

      {processing && (
        <ProgressBar
          current={progress.current}
          total={progress.total}
          status={progress.status}
          onCancel={handleCancelOperation}
        />
      )}

      <form onSubmit={handleSearch}>
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          {/* Main Filters */}
          <div className="filter-section">
            <div className="filter-group">
              <label className="filter-label">Product Type</label>
              <MultiSelectDropdown
                options={productTypeOptions}
                selected={searchFilters.product_types}
                onChange={values => handleFilterChange('product_types', values)}
                placeholder="All Types"
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Collection</label>
              <MultiSelectDropdown
                options={collectionOptions}
                selected={searchFilters.collection_ids}
                onChange={values => handleFilterChange('collection_ids', values)}
                placeholder="All Collections"
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Min Price</label>
              <input
                type="number"
                className="form-input"
                placeholder="0.00"
                value={searchFilters.price_min}
                onChange={e => handleFilterChange('price_min', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Max Price</label>
              <input
                type="number"
                className="form-input"
                placeholder="99999"
                value={searchFilters.price_max}
                onChange={e => handleFilterChange('price_max', e.target.value)}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="filter-actions" style={{ justifyContent: 'flex-start', gap: 'var(--space-3)' }}>
            <button type="button" className="btn btn-secondary" onClick={clearFilters}>
              Clear
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Loading...' : 'Search'}
            </button>
            {allItems.length > 0 && (
              <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
                Showing {displayItems.length} of {allItems.length} zero-inventory products
              </span>
            )}
          </div>
        </div>
      </form>

      {/* Bulk Actions */}
      {displayItems.length > 0 && (
        <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
          <button
            className="btn btn-danger"
            onClick={handleUnlistSelected}
            disabled={selectedItems.size === 0 || processing}
          >
            Unlist Selected ({selectedItems.size})
          </button>
        </div>
      )}

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : !hasSearched ? (
          <div className="empty-state">
            <div className="empty-state-title">Search Zero-Inventory Products</div>
            <div className="empty-state-description">Use the filters above and click "Search" to find products with zero inventory.</div>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Zero-Inventory Products</div>
            <div className="empty-state-description">All active products have inventory.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={selectedItems.size === displayItems.length && displayItems.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th style={{ width: 60 }}>Image</th>
                <SortHeader field="title">Title</SortHeader>
                <SortHeader field="product_type">Type</SortHeader>
                <th>SKU</th>
                <SortHeader field="created_at">Created</SortHeader>
                <SortHeader field="cost" align="right">Cost</SortHeader>
                <SortHeader field="price" align="right">Price</SortHeader>
                <th style={{ width: 120 }}>Search</th>
                <th style={{ width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map(item => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                    />
                  </td>
                  <td>
                    {item.images && item.images[0] ? (
                      <img src={item.images[0].src} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }} />
                    ) : (
                      <div style={{ width: 48, height: 48, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-gray-400)', fontSize: 11 }}>No img</div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
                  </td>
                  <td className="text-sm">{item.product_type || '—'}</td>
                  <td className="text-sm text-muted">{item.sku || '—'}</td>
                  <td className="text-sm text-muted">{new Date(item.created_at).toLocaleDateString('en-GB')}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(item.cost)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(item.price)}</td>
                  <td><SearchLinks title={item.title} /></td>
                  <td>
                    <a href={`${SHOPIFY_ADMIN_URL}/products/${item.id}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary">View</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Per-page selector */}
        {displayItems.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <span className="text-sm text-muted">
              Showing {displayItems.length} of {allItems.length} loaded
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span className="text-sm text-muted">Per page:</span>
              {[25, 50, 100, 200].map(num => (
                <button
                  key={num}
                  type="button"
                  className={`btn btn-sm ${limit === num ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setLimit(num)}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default InventoryCleanup
